import React, { useEffect, useRef, useState} from 'react';
import ReactSwipe from 'react-swipe';
import { Trash as TrashIcon, CircleCheck as CircleCheckIcon, ArrowUp, X as XIcon } from 'lucide-react';

import { swipeDetect } from '../utils/detect';
import { getRandomColor, isAudio, isImage, isPdf, isVideo, loadCss, loadScript, sanitizeToId, sanitizeToUnderscoreId, useRerender, useRerenderWithValue } from '../utils/common';
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

import PROFILE_GENERIC_AVATAR from "../images/avatar.png"
import { COUNTRY_PHONE_CODES_LIST, IS_MOBILE } from '../constants';
import { getUKey, Offset, scrollLeft, scrollTop } from './utils';
import { addScrollListener, removeScrollLister, getScrollParent } from '../utils/scrolling';
import { Popup } from './popups';
import { getByIds } from '../get_by_ids';
import { getKeyPath, hashCode, idToTitle, isFunction, isObject, isString, last, len, setKeyPath, toTitleCase } from '../utils';
import InfoIcon  from "../images/Info.svg";
import { broadcaster, useBroadcastedState, useLocalStorageState } from '../utils/events';
import { EditableTable } from './editable_table';

const _colors = ["red", "deep-orange", "purple", "teal", "yellow"];
var CSS_COLOR = (str) => {    
    return _colors[hashCode(str) % _colors.length];
}

function ShortImageAndDescription({images, title, description, tag, action_button}){
    return (
        <div className="w3-flex-row w3-list-horizontal">
            {
                images && images.length > 1
                ?   <ReactSwipe className="w3-col s3">
                        {images.map((img, i) => <img key={i} src={img.url || img} className="w3-image" /> )}
                    </ReactSwipe>
                :   <div className="w3-col s3">
                        {images?.[0] && <img src={images[0].url || images[0]} className="w3-image" />}
                    </div>
            }
            
            <div className="w3-flex-grow-s1">
                <div className="w3-bold">{title}</div>
                {
                    description
                    ?   <div className="w3-text-grey w3-small">{description}</div>
                    :   null
                }
                {
                    tag 
                    ?   <div className={
                            `w3-tag w3-${CSS_COLOR(tag + 'xxx')}`
                        }>{tag}</div> 
                    :   null
                }
            </div>
            {
                action_button
                ?   <div className="w3-center">
                        <a className="w3-decoration-none w3-button w3-2px-shadow w3-round"
                            href={action_button.url}
                        >
                            {action_button.title || title.substr(50)}
                        </a>
                        {
                            action_button.description
                            ?  <div className="w3-small">{action_button.description}</div>  
                            :   null
                        }
                    </div>
                : null
            }
        </div>
    )
}

function HorizontalUsersList({user_ids}){
    const [users, setUsers] = useState(null);
    useEffect(
        () => {
            if(!user_ids?.length) return;
            getByIds({"user_ids": user_ids}).then((data) => {
                setUsers(user_ids.map((user_id) => data.users[user_id]));
            });
        }, [user_ids]
    );
    if(!users) return null;
    return <div className='w3-flex-row-wrap'>
        {users.map((user, i) => <UserShortBadge user={user} />)}            
    </div>
}

function VerticalUsersList({user_ids}){
    const [users, setUsers] = useState(null);
    useEffect(
        () => {
            if(!user_ids?.length) return;
            getByIds({"user_ids": user_ids}).then((data) => {
                setUsers(user_ids.map((user_id) => data.users[user_id]));
            });
        }, [user_ids]
    );
    if(!users) return null;
    return <div className='w3-flex-col w3-list w3-list-bordered'>
        {
            users.map((user, i) => <div className='w3-flex-row'>
                    <div className='w3-padding-4'>
                        <img className="w3-circle w3-image" style={{height: "25px", width:"25px"}} 
                            src={user.image?.url || user.image || PROFILE_GENERIC_AVATAR}
                            alt={user.name}
                            title={user.name}
                        />
                    </div>
                    <div className='w3-padding-4'>{user.name}</div>
                </div>
            )
        }            
    </div>
}

function CrammedUsersList({user_ids, options}){
    const [users, setUsers] = useState(null);
    const [highlighed_user_index, setHighlightedUserIndex] = useState(0);
    useEffect(
        () => {
            if(!user_ids?.length) return;
            getByIds({"user_ids": user_ids}).then((data) => {
                setUsers(user_ids.map((user_id) => data.users[user_id]));
            });
        }, [user_ids]
    );
    useEffect(
        () => {
            /* just highlight users one by one  */
            if(!users?.length) return;
            var i = 0;
            const interval = setInterval(
                () => setHighlightedUserIndex(++i % users.length),
                2000
            );
            return () => clearInterval(interval);
        }, [users]
    );

    if(!users?.length) return null;
    /* crammed list of user images */
    return <div>
        <div  className='w3-relative' style={{minHeight: "27px"}}>
            {
                users.map((user, i) => 
                    <div className="w3-absolute w3-white w3-border w3-circle"
                        key={user._id}
                        style={{padding:"1px", left: (20 * (i++)) + "px"}}
                    >
                        <img className="w3-image w3-circle" style={{height: "25px", width:"25px"}}
                            src={user.image?.url || user.image || PROFILE_GENERIC_AVATAR}
                            alt={user.name}
                            title={user.name}
                        />
                    </div>
                )
            }
        </div>
        <div className='w3-padding-topbottom-4 w3-text-grey w3-animate-opacity' key={highlighed_user_index}>
            {users[highlighed_user_index].name}
        </div>
    </div>
}

function RoundUserImage({user, className}){
    if(!user) return null;
    let user_image = user.image?.url || user.image;
    return (
        <div className={`avatar ${className}`}>
            <div className="tw-text-[16px] tw-font-semibold tw-text-primary-v2">
                {
                    user_image
                    ?   <img className="tw-w-full tw-h-full tw-object-cover" src={user_image}/>
                    :   user.name?.slice(0,1)
                }
            </div>
        </div>
    );
}

function UserBadge({user}){
    if(!user) return;
    return (
        <div key={user._id} className="tw-flex">
            <RoundUserImage user={user} />
            <div className='tw-flex-grow tw-ml-[8px]'>
                <div className="tw-capitalize tw-font-medium tw-text-[12px] tw-leading-[20px] tw-text-primary-text">{user.alt_name || user.name}</div>
                <div className='tw-font-medium tw-text-[11px] tw-text-secondary-text'>{user.phone_number || user.email_id}</div>
            </div>
        </div>
    );
}



function UserShortBadge({user, tag}){
    return (
        <div className="w3-padding-4 w3-inline w3-center" style={{maxWidth: "60px"}}>
            <div className="w3-center w3-padding-topbottom-4">
                <div className="w3-relative w3-inline w3-padding-2 w3-border w3-circle">
                    <img className="w3-circle w3-image"
                        src={user.image ? user.image.url || user.image : PROFILE_GENERIC_AVATAR}
                        style={{width: "30px", height:"30px"}}
                    />
                </div>
            </div>
            <div className="w3-center w3-padding-bottom-4 w3-super-tiny">
                <div className="w3-text-grey w3-bold w3-capitalize">{user.name}</div>
                {
                    tag 
                    ?   <div className={
                            `w3-tag w3-${CSS_COLOR(tag + 'xxx')}`
                        }>{tag}</div> 
                    :   null
                }
            </div>
        </div>
    )
}


/* table input */
function RowInput({row, nCols, onAction, placeholders, input_types}){
    for(let i=row.length;i<nCols;i++){
        row.push("");
    }
    return <div className="w3-flex-row w3-list-horizontal-8">
        {
            row.map(
                (val, i) => {
                    return (
                        <div key={i}>
                            <input type={input_types ? input_types[i] : "text"} className="input-primary"
                                onChange={(evt) => {row[i] = evt.target.value; onAction("change");}}
                                placeholder={placeholders[i]}
                                defaultValue={val}
                            />
                        </div>
                    )
                }
            )
        }
        <div className="w3-col s2 tw-m-2 w3-flex w3-list-horizontal-16">
            <TrashIcon size={18} onClick={()=> {onAction("delete", row)}} />
            <ArrowUp size={20} onClick={()=> {onAction("moveup", row)}} />
        </div>
    </div>
}

function SimpleTableInput({rows:_rows, nCols, placeholders, inputType: input_types, onAction, headers = []}){
    const rerender = useRerender();
    placeholders = placeholders || [];
    nCols = nCols || (_rows && _rows.length && _rows[0].length) || 2;
    return (
        <div className="w3-row w3-border w3-padding-8">
            {
                headers?.length 
                ? <div className='w3-flex-row w3-margin-bottom-4'>
                    {headers?.map((header) => {
                        return (
                            <div className='w3-flex-row w3-flex-vcenter tw-w-[29%]' 
                            style={{ gap: header?.info_text ? 
                                "8px" 
                                : "0"
                            }} 
                            key={header?.title}
                            >
                                <p className='tw-text-sm tw-mt-[3px]'>{header.title}</p>
                                {header?.info_text ? <InfoText text={header?.info_text} /> : null}
                            </div>
                        )
                    })}
                </div>
                : null
            }
            {
                _rows.map(
                    (row, i) => <RowInput
                        row={row} nCols={nCols} key={getUKey(row)}
                        placeholders={placeholders}
                        onAction={(action, row)=>{
                            if(action === "delete"){
                                _rows.splice(_rows.indexOf(row), 1);
                                rerender();
                            } else if(action == "moveup"){
                                let index = _rows.indexOf(row);
                                if(index > 0){
                                    _rows.splice(index, 1);
                                    _rows.splice(index - 1, 0, row);
                                    rerender();
                                }
                            }
                            onAction?.(action, row, _rows);
                        }}
                        input_types={input_types}
                    />
                )
            }
            <div className="w3-padding-8">
                <button className="btn-secondary"
                    onClick={() => {
                        _rows.push(new Array(nCols).fill(""));
                        rerender();
                        onAction?.("add", _rows[_rows.length - 1], _rows);
                    }}
                >Add</button>
            </div>
        </div>
    );
}

/**
 * DynamicInput
 * A component for rendering different types of input fields based on the column properties.
 *
 * @param {any} defaultValue - The initial value of the input field.
 * @param {function} onChange - A function to handle changes in the input field.
 * @param {Object} col - The column properties.
 * @param {string} col.type - The type of the input field. It can be 'textarea', 'boolean', 'select', 'text', or 'number'.
 * @param {Array} col.list - The list of options for the 'select' type.
 * @param {string} col.placeholder - The placeholder text for the input field.
 * @param {Object} col.props - Additional properties for the input field.
 *
 * @returns {JSX.Element} - The input field based on the column properties.
 */function DynamicInput({defaultValue, onChange, col}) {
    const { type, list, placeholder} = col;
    if (type === 'textarea') {
        return <textarea defaultValue={defaultValue} {...col.props || {}}
            onChange={(evt) => onChange(evt.target.value)} className='w3-input' placeholder={placeholder} />
    } else if (type === 'boolean') {
        return <input type='checkbox' defaultChecked={defaultValue}
            onChange={(evt) => onChange(evt.target.checked)} className='w3-input'
        />
    } else if (type === 'select' && list?.length) {
        return <select defaultValue={defaultValue}
            onChange={(evt) => onChange(evt.target.value)} className='w3-input' placeholder={placeholder}>
            {list.map((item) => {
                return typeof item === 'object'
                    ? <option key={item.value} value={item.value}>{item.label}</option>
                    : <option key={item} value={item}>{item}</option>
            })}
        </select>
    } else if (type === 'text' || type == 'number') {
        return <input type={type} defaultValue={defaultValue} {...col.props || {}}
            onChange={(evt) => onChange(evt.target.value)} className='w3-input' placeholder={placeholder} />
    }
    return null;
}

/**
 * A component for displaying and editing tabular data.
 *
 * @param {Object} props - The properties for the component.
 * @param {Array} props.rows - The initial rows of data.
 * @param {Array} props.cols - The columns of data. Each column is an object with a title and an id.
 *
 * @returns {JSX.Element} - The TableInput component.
 */
function TableInput({rows:_rows, cols, maxRows}){
    const [rows, setRows] = useState(_rows);

    const handleChange = (index, _id) => {
        return (value) => {
            rows[index][_id] = _rows[index][_id] = value;
        }
    }

    return (
        <>
        <table className='w3-table w3-table-all w3-cell-bordered'>
            <thead>
                <tr>
                    {cols.map(col => (
                        <th key={`th_${col._id}`}>{col.title}</th>
                    ))}
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((row, i) => (
                    <tr key={getUKey(row)}>
                        {cols.map((col) => (
                            <td key={`td_${col._id}`}>
                                <DynamicInput col={col} defaultValue={row[col._id]} onChange={handleChange(i, col._id)} />
                            </td>
                        ))}
                        <td className=''>
                            <div className='tw-flex tw-items-center tw-gap-x-2'>
                                <TrashIcon size={18} onClick={()=> {_rows.splice(i, 1); setRows([..._rows]);}} />
                                {i === 0 ? null : <ArrowUp size={20} onClick={()=> {_rows.splice(i, 1); _rows.splice(i - 1, 0, row); setRows([..._rows]);}} className=" w3-text-blue" />}
                            </div>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
        {(!maxRows || rows.length < maxRows) && (
            <div className="w3-right-align w3-padding-8">
                <div className="w3-button w3-green w3-small"
                    onClick={() => {
                        _rows.push({});
                        setRows([..._rows]);
                    }}
                >Add</div>
            </div>
        )}
        </>
    );
}


/* Editable attr text field, with a checkbox to enable/disable */
function NamedCheckboxAttribute({attrs:_attrs, _key, title, onAttrsChanged, default_value, placeholder}){
    const [attrs, setAttrs] = useState(_attrs || {});
    return (
        <label className="w3-flex w3-flex-vcenter w3-row">
            <input type="checkbox"
                checked={!!attrs[_key]}
                className="w3-check"
                onChange={
                    (evt) => {
                        if(!evt.target.checked){
                            delete attrs[_key];
                        }
                        else{
                            attrs[_key] = default_value;
                        }
                        let _attrs = {...attrs};
                        setAttrs(_attrs);
                        onAttrsChanged(_attrs);
                    }
                }
            />
            <span className="w3-padding-sides-8">{title}</span>
            {
                attrs[_key]
                ?   <input type="text" name={_key} className="w3-input-inline" placeholder={placeholder || "Name"} 
                        onChange={
                            (evt) => {
                                attrs[_key] = evt.target.value;
                                let _attrs = {...attrs};
                                setAttrs(_attrs);
                                onAttrsChanged(_attrs);
                            }
                        }
                    />
                :   null
            }
        </label>
    );
}

/* Editable attr text field, with a checkbox to enable/disable */
function EnabledWithCheckbox({children, name, checked:_checked, onChange, disabled, icon, forceCheckState}){
    const [checked, setChecked] = useState(_checked);
    useEffect(()=>{forceCheckState && setChecked(_checked)}, [_checked, forceCheckState]);

    return (
        <fieldset className={`tw-flex tw-flex-col tw-space-y-2 tw-gap-2 ${checked ? "" : "tw-px-0 tw-border-0"}`}>
            <legend className={`tw-flex tw-gap-2.5 tw-text-sm tw-items-center`}>
                <input
                    className="input-primary"
                    type="checkbox"
                    checked={!!checked}
                    disabled={!!disabled}
                    onChange={(e) => {
                        setChecked(e.target.checked);
                        onChange?.(e.target.checked);
                    }}
                />
                 
                <div className='tw-flex tw-flex-wrap tw-items-center tw-gap-x-2.5'>
                    {icon ? icon : null}
                    {name}
                </div>
            </legend>
            {checked ? children : null}
        </fieldset>
    );
}

/* Display As pages navigatable  */
function PageIndicator({page, className}){
    return (
        <div className={"w3-flex-row-wrap w3-flex-hcenter w3-flex-vcenter w3-array-margin w3-pointer w3-border " + (className || "")}
            style={{height: "40px", width: "40px"}}
            page={page}
        >
            <div className='w3-padding-4'>{page}</div>
        </div>
    )
}
function PagesSelection({page:_page, total_pages, onPageSelected}){
    const [page, setPage] = useState(_page);
    const [num_right_pages, setRightNumPages] = useState(5);
    const [num_left_pages, setLeftNumPages] = useState(5);
    
    const prev_pages = [];
    for(let i=Math.max(1, page - num_left_pages);i<page;i++){
        prev_pages.push(i);
    }
    // next pages
    const next_pages = [];
    for(let i=page + 1;i<=Math.min(total_pages, page + num_right_pages);i++){
        next_pages.push(i);
    }

    const handlePageSelect = (evt) => {
        let parent = evt.target;
        while(parent && parent != evt.currentTarget){
            let page_attr = parent.getAttribute("page");
            if(page_attr){
                let selected_page = parseInt(page_attr);
                setPage(selected_page);
                onPageSelected(selected_page);
                return;
            }
            parent = parent.parentElement;
        };    
    }

    return (
        <div className='w3-flex w3-flex-row-wrap' onClick={handlePageSelect}>
            {
                prev_pages.length && prev_pages[0] != 1
                ?   <PageIndicator page={1} />
                :   null
            }           
            {
                prev_pages.length && prev_pages[0] > 2
                ?  <div className='w3-inline' onClick={() => {setLeftNumPages(num_left_pages + 5)}}>.....</div>
                :   null
            }
            {/*previous pages*/}
            {
                prev_pages.map(
                    (i)=> <PageIndicator page={i} key={i} />
                )
            }
            {/*current page*/}
            <PageIndicator page={page} className="w3-border-green" />
            {
                next_pages.map(
                    (i)=> <PageIndicator page={i} key={i} />
                )
            }
            {
                next_pages.length && next_pages[next_pages.length - 1] < total_pages
                ?  <div className='w3-inline' onClick={() => {setRightNumPages(num_right_pages + 5)}}>.....</div>
                :   null
            }
            {
                next_pages.length && next_pages[next_pages.length - 1] != total_pages
                ?   <PageIndicator page={total_pages} />
                :   null
            }
        </div>
    )
}


/* downloaded files  */
export function ThumbnailFileView({file, className, onClick}){
    let file_url = file.thumbnail || file.url || file;
    const extension = file_url?.split('.').pop().toLowerCase();

    if(isImage(file)){
        return <img
            src={file_url}
            alt="Image Unavailable"
            className={`w3-image-inline w3-bounded w3-pointer ${className || ''}`}
            onClick={() => onClick ? onClick() : Popup.show("Image", <div className='w3-flex w3-flex-hcenter'><PinchZoomableImage file_url={file_url} /></div>)}
        />
    } 
    if(isAudio(file)){
        return (
            <audio controls className={`w3-bounded ${className || ''}`} preload="none">
                <source src={file_url} type={isAudio(file_url)} />
            </audio>
        )
    }
    const is_video_mime_type = isVideo(file)
    if(is_video_mime_type){
        return (
            <video style={{maxWidth: "100%"}} className='w3-image-inline w3-bounded w3-pointer tw-aspect-video' controls>
                <source src={file_url} type={is_video_mime_type} />
                Your browser does not support the video tag.
            </video>
        ); 
    }
    if(isPdf(file)) {
        return (
            <div className='w3-pointer' onClick={() => window.open(file_url, '_blank')}><object data={file_url} className='w3-image-inline w3-bounded' style={{pointerEvents: 'none'}} /></div>
        ); 
    }
    return  (
        <div style={{height: "60px", maxWidth: "200px"}} className={`w3-relative ${className || ''}`}>
            <div className="w3-display-middle tw-w-full">
                <a href={file_url} target="_blank" rel="noreferrer" className='tw-flex tw-flex-col tw-items-center'>
                    Download <p className='tag-primary'>{extension && `.${extension}`}</p>
                </a>
            </div>
        </div>
    );
}

export function PinchZoomableImage({file_url}){
    return (
        <TransformWrapper doubleClick={{"disabled": IS_MOBILE}}>
            <TransformComponent>
                <img alt={"No Image"} src={file_url} style={{maxHeight: "500px", maxWidth: "100%"}}/>
            </TransformComponent>
        </TransformWrapper>  
    );
}
export function FileView({file}){
    let file_url = file.thumbnail || file.url || file;
    if(isImage(file)){
        return <PinchZoomableImage file_url={file_url}/>;
    }
    if(isAudio(file)){
        return (
            <audio controls className="w3-bounded" preload="none">
                <source src={file} type={`audio/${isAudio(file)}`} />
            </audio>
        )
    }
    const is_video_mime_type = isVideo(file);
    if(is_video_mime_type){
        return <video style={{maxHeight: "300px", maxWidth: "100%"}} autoPlay>
            <source src={file_url} type={is_video_mime_type} />
            Your browser does not support the video tag.
        </video>
    }
    return  (
        <div style={{height: "300px"}} className="w3-relative">
            <div className="w3-display-middle">
                <a href={file_url} target="_blank" rel="noreferrer">
                    Download
                </a>
            </div>
        </div>
    );
}

/* displays a gallery view of given files */
export function Gallery({files: _files, className, cur_index}){
    const files = _files?.filter(file => file) || [];

    const [current_file, setCurrentFile] = useState(cur_index || 0);

    const displayFile = (evt) => {  // delegate
        let i = evt.target.parentElement.getAttribute("i");
        if(!i) return;
        setCurrentFile(parseInt(i))
    }

    const displayed_file = useRef();
    useEffect(
        () => {
            if(!files.length) return;
            return swipeDetect(
                displayed_file.current,
                (direction) => {
                    let next_file = Math.min(
                        Math.max(
                            0, current_file + (direction === "left" ? -1 : 1)
                        ),
                        files.length - 1
                    )
                    setCurrentFile(next_file)
                }
            );     
        },
        [displayed_file, current_file, files.length]
    );

    if(!files.length) return null;
    const file = files[current_file];
    return (
        <div className={`w3-row ${className || ""}`}>
            {
                file 
                ?   <div className='w3-flex w3-flex-hcenter w3-margin-bottom-8' ref={displayed_file}>
                        <FileView file={file} />
                    </div>
                : null
            }
            {
                files.length > 1
                ?   <div className='tw-flex tw-gap-3 tw-overflow-x-auto tw-scroll-smooth tw-snap-x tw-snap-mandatory tw-p-2 md:no-scrollbar' onClick={displayFile}>
                        {
                            files.map(
                                (file, i) => 
                                    <div 
                                        className={`
                                            tw-flex-shrink-0 tw-w-[100px] tw-h-[100px] tw-rounded-md tw-border tw-relative tw-overflow-hidden
                                            tw-snap-center
                                            ${i === current_file ? "tw-ring-2 tw-ring-blue-500" : "hover:tw-ring-1 hover:tw-ring-gray-400"}
                                          `}
                                        i={i}
                                        key={file.thumbnail || file.url || file}
                                    >
                                        <ThumbnailFileView file={file} onClick={() => setCurrentFile(i)}/>
                                    </div>
                            )
                        }
                    </div>
                :   null
            }
        </div>
    );
}

/* A tooltip components on a element(target) */
function ToolTip({target, children, ctx, options}){

    const _tooltip = useRef();
    const [_style, setStyle] = useState({});
    const [_class_name, setClassName] = useState("");

    const [_render, rerender] = useRerenderWithValue();     
    ctx.rerender = rerender;
    options = options || {};

    useEffect(
        () => {
            let tooltip = _tooltip.current;

            let target_top = Offset(target).top - scrollTop(window);
            let target_left = Offset(target).left - scrollLeft(window);

            let style = {
                "zIndex": 50,
                "position": "fixed",
                "maxWidth": window.innerWidth - 60,
                "minWidth": "100px",
                "minHeight": "30px",
                "maxHeight": window.innerHeight - 60,
            };

            let class_name = "";
            let target_width = target.offsetWidth || 10;
            let target_height = target.offsetHeight || 20;
            let pos_left = target_left + ( target_width / 2 ) - ( tooltip.offsetWidth / 2 );
            let pos_top  = target_top - tooltip.offsetHeight - 20;
            // let pos_bottom = target_top + target_height + tooltip.offsetHeight + 20;
            let _right = "unset"; 
            let _bottom = "unset";
            let _top = pos_top + 10;
            let _left = pos_left;
        
            if(pos_left < 0){
                pos_left = target_left + target_width / 2 - 20;
                _left = pos_left;
                _right = 'unset';
                class_name +=  ' tleft ';
            }
            else{
                class_name.replace('tleft', "");
            }
            if( pos_left + tooltip.offsetWidth > window.innerWidth ){
                class_name += ' tright';
                _right = window.innerWidth - target_left - target_width / 2 - 20;
                _left = "unset";
            }
            else{
                class_name.replace('tright', "");
            }
            
            let bottom_free_space = window.innerHeight - (target_top + target_height);
            let top_free_space = target_top;
            
            let hang_down = top_free_space < bottom_free_space;
            if(hang_down){  
                _top  = target_top + target_height + 10;
                class_name.replace(' tbottom', "");
                class_name += ' ttop';
            }
            else{
                class_name.replace('ttop', "");
                class_name += ' tbottom';
                _top = "unset";
                _bottom  = window.innerHeight - target_top + 10;
            }

            Object.assign(style,  {left: _left, top: _top, bottom: _bottom, right: _right });
            setClassName(class_name);
            setStyle(style);
        }, [_render, _tooltip.current]
    );

        
    useEffect(
        () => {
            const container = getScrollParent(target, 'y', 1);
            /* if clicked outside of this tooltip close this */
            const closeTooltip = (evt) => {
                const is_click_inside = _tooltip.current?.contains(evt.target);
                if(!is_click_inside && options.no_auto_close) return;
                if (is_click_inside && options.multi_clickable ) return;
                ctx.close && ctx.close()
            };
            document.body.addEventListener("click", closeTooltip);

            /* if the container was scrolled, we reset the position of the tooltip */
            const cbs = {
                "on_scroll_stop": () => {rerender();}
            }
            addScrollListener(container, cbs);
            /* cleanup */
            return () => {
                removeScrollLister(container, cbs);
                document.body.removeEventListener("click", closeTooltip);
            }
        },
        []
    );
    
    return (
        <div className={`w3-round-large w3-shadow w3-pointer-tooltip ${_class_name}`}
            style={_style} ref={_tooltip}
        >
            {children}
        </div>
    );
}


/* Share Button */
const _file_bytes_cache = {};

function Share({title, description, link, file_url, cb, children, className}){
    title = title || "";
    description = description || "";

    var share_text = (title || "");
    if(description){
        share_text += ("\n" + description);
    }

    const [status_text, setStatusText] = useState(null);

    useEffect(
        () => {
            const timer = setTimeout(() => {
                setStatusText(null);
            }, 1000);
            return () => clearTimeout(timer);
        },
        [status_text]
    );

    var after_file_downloaded = function(file_bytes) {
        let mime_type = null;
        let file_name = null;
        let file_url_obj = new URL(file_url, window.location.href);
        if(file_url_obj.pathname.endsWith("mp3")){
            mime_type = "audio/mp3";
            file_name = title + ".mp3";
        }
        else if(file_url_obj.pathname.endsWith("opus")){
            mime_type = "audio/opus";
            file_name = title + ".opus";
        }
        else if(file_url_obj.pathname.endsWith("jpeg") ||
                file_url_obj.pathname.endsWith("jpg")
        ){
            mime_type = "image/jpeg";
            file_name = title + ".jpeg";
        }
        else if(file_url_obj.pathname.endsWith("png")){
            mime_type = "image/png";
            file_name = title + ".png";
        }
        file_name = file_name.replace(/\s/g,"-");

        let file_data = _file_bytes_cache[file_url] = new File(
            [file_bytes], 
            file_name, 
            {
                type: mime_type,
            }
        );

        if(link){
            /* appending link because url param doesn't work fully yet */
            share_text += "\n" + link;
        }
        let to_share = {
            files: file_data ? [file_data]: [],
            title: title,
            text: share_text,
            url: link  // does not work fully yet
        };
        if (navigator.canShare(to_share)){
            navigator.share(to_share).then(function(){
                /*share successful*/
                cb && cb(true);
            }).catch(function(err){
                if(err && err.toString().indexOf("NotAllowedError") != -1){
                    navigator.share({
                        title: title,
                        text: share_text,
                        url: link
                    });
                }
                cb && cb(false);
            });
            setStatusText("sharing..");
        }
        else{
            /*
                unable to share files so share whatever we can 
                and call it successs
            */
            navigator.share({
                title: title,
                text: share_text,
                url: link
            }).catch(function(err){

            });
            setStatusText("sharing link..");
            cb && cb(true);
        }
        // TODO: Enable download
    }

    const doShare = () => {
        if(file_url && navigator && navigator.canShare){ // share file

            let _cached_file = _file_bytes_cache[file_url];
            if(_cached_file){
                after_file_downloaded(_cached_file);
                return;
            }

            setStatusText("sharing..");

            let to_download = new URL(file_url, window.location.href).href;
            if(to_download.indexOf("?") < 0){
                to_download+="?";
            }
            to_download += ("&_rand_=" + parseInt(new Date().getTime())); // add random to prevent caching
            var req = new XMLHttpRequest();
            req.open("GET", to_download, true);
            req.responseType = "arraybuffer";

            req.onprogress = function (evt) {
                if(evt.total){
                    setStatusText(parseInt(evt.loaded/evt.total * 100) + "% download");
                }
            };
            req.onerror = function(evt){
                setStatusText("Error Downloading");
                cb && cb(false);
            };
            req.onload = function(evt){
                after_file_downloaded(req.response);
            };
            req.send(null);
        } else if(link && navigator && navigator.share){ // share url
            navigator.share({
                title: title,
                text: share_text,
                url: link
            }).catch(function(err){
                
            });
            setStatusText("Sharing...");
            cb && cb(true);
        } else{
            cb && cb(false);
            window.open(link || file_url, "_blank", "noreferrer");
        }
    }
    
    return (
        <div className={className} onClick={
            (evt) => doShare()
        }>
            {status_text || children}
        </div>
    );
}

/* 
    Enables tag selections
    tag -> {title, value, selection}
    selection -> {title, value} # override to alt selection when selected
    selection -> null -> text search
*/
function TagsInput({
    selected:_selected, placeholder, suggestions,
    onSelectedChange, onTextSearch, className, options, renderSelected, storage_key
}){
    const [selected, setSelected] = useState([]);
    const [stored_selected, setStoredSelected] = useLocalStorageState(
        storage_key || "__tags_input_no_storage__",
        null
    );
    const [filtered_suggestions, setFilteredSuggestions] = useState(null);
    const [highlighted_suggestion, setHighlightedSuggestion] = useState(-1);
    const [search_text, setSearchText] = useState("");
    const input_el_ref = useRef();
    const ctx = useRef({"deleted": []}).current;
    const rerender = useRerender();
    ctx.highlighted_suggestion = highlighted_suggestion;

    useEffect(
        () => {
            if(_selected?.length){
                setSelected(_selected);
                storage_key && setStoredSelected(_selected);
            } else if(storage_key && stored_selected){
                setSelected(stored_selected);
                onSelectedChange?.(stored_selected);
            }
        },
        [_selected, storage_key, stored_selected]
    );
    useEffect(() => {
        /* we show the filtered results only when focused */
        document.body.addEventListener("click", rerender); 
        return () => document.body.removeEventListener("click", rerender);
    }, []);

    const updateSelected = (new_selected) => {
        onSelectedChange?.(new_selected);
        setSelected(new_selected);
        storage_key && setStoredSelected(new_selected);
    }

    const addSelection = (v) => {
        if(v.selection !== undefined){
            /* overridden/custom selection when clicked */
            if(v.selection){
                addSelection(v.selection);
            } else {
                doTextSearch(search_text);
            }
            return;
        }
        let new_selected = [
            ...selected.filter(item => !item?.tag || item?.tag !== v?.tag),
            v
        ];
        updateSelected(new_selected);
        setSearchText("");
        setFilteredSuggestions(null);
    }

    const removeSelection = (v) => {
        if(v?.can_remove === false) return;
        let new_selected = selected.filter((_v) => _v !== v)
        updateSelected(new_selected);
        v && ctx.deleted.push(v);
    }

    const doTextSearch = (text) => {
        onTextSearch && onTextSearch(text);
        ctx.last_text_search = text;
        setFilteredSuggestions(null);
    }

    /* handle enter / arrow keys to cycle through */
    useEffect(
        () => {
            const handleKeyDown = (evt) => {
                if(!filtered_suggestions) return;
                const highlighted_suggestion = ctx.highlighted_suggestion;
                if(evt.key === "Enter"){
                    if(
                        filtered_suggestions && filtered_suggestions.length
                        && !ctx.debounced_search_timer
                    ){
                        addSelection(
                            filtered_suggestions[highlighted_suggestion]
                            || filtered_suggestions[0] 
                        );
                    }
                } 
                //escape
                else if(evt.key === "Escape"){
                    setFilteredSuggestions(null);
                }
                // arrow down
                else if(evt.key === "ArrowDown"){
                    setHighlightedSuggestion((highlighted_suggestion + 1) % filtered_suggestions.length);
                } 
                else if(evt.key === "ArrowUp"){
                    setHighlightedSuggestion(highlighted_suggestion > 0 ? highlighted_suggestion - 1 : -1);
                }
            }
            setHighlightedSuggestion(-1);
            document.addEventListener("keydown", handleKeyDown);
            return () => document.removeEventListener("keydown", handleKeyDown);
        },
        [filtered_suggestions]
    );

    const showSuggestionsForSearchText = (search_text) => {
        if(isFunction(suggestions)){
            suggestions(search_text).then((s) => {setFilteredSuggestions(s);});
        } else {
            setFilteredSuggestions(
                suggestions?.filter(
                    (s) => {
                        let _s = s.title || s._id || s;
                        return _s.toLowerCase().includes(search_text.toLowerCase())
                    }
                )
            );
        }
    }

    useEffect(
        () => {        
            const debouncedCallback = () => {
                if(!search_text) {
                    setFilteredSuggestions(null);
                    return;
                }
                showSuggestionsForSearchText(search_text);
                ctx.debounced_search_timer = null;
            }
            if(ctx.debounced_search_timer) clearTimeout(ctx.debounced_search_timer);
            ctx.debounced_search_timer = setTimeout(debouncedCallback, 300);
        }, [search_text]
    );

    const onTextInputKeyUp = (evt) => {
        if(evt.key === 'Backspace' || evt.key === 'Delete'){
            let last_selected = last(selected);
            if(!search_text){
                if(last_selected){
                    if(!ctx.backspaace_pressed_twice) ctx.backspaace_pressed_twice = 1;
                    else {removeSelection(last_selected); ctx.backspaace_pressed_twice = 0;}
                } else if(ctx.last_text_search){
                    doTextSearch("");
                    ctx.last_text_search = null;
                }
            } else{
                ctx.backspaace_pressed_twice = 0;
            }
        }
        /* check ctrl-z */
        else if(evt.ctrlKey && evt.key === 'z'){
            let last_deleted = last(ctx.deleted);
            if(last_deleted){
                addSelection(last_deleted);
                ctx.deleted.pop();
            }
        }
    }

    return (
        <div className={`w3-border w3-input w3-flex-row-wrap tw-space-1 w3-white tw-relative ${className || ''}`}
            onClick={(evt) => {
                (
                    evt.target === input_el_ref.current?.parentElement
                    || evt.target == evt.currentTarget
                ) && input_el_ref.current?.focus()
            }}
        >
            {
                selected.map((v) => {
                    const title = v.title || v._id || v;
                    if(renderSelected){
                        return renderSelected(v, () => removeSelection(v));
                    }
                    return (
                        <div className='tag-tertiary w3-no-wrap w3-margin-right-8' 
                            style={{backgroundColor: getRandomColor(title, 190)}}
                            key={title}
                        >
                            {
                                v.can_remove === false
                                    ? title
                                    : <>
                                        <XIcon size={14}
                                            onClick={(evt) => removeSelection(v)} />
                                        &nbsp;{title}
                                    </>
                            }
                        </div>
                    )
                })
            }
            {
                !options?.max_selections || selected.length < options.max_selections 
                ?   <div className='w3-flex-grow-s1 w3-relative'>
                        <input className="w3-border-none w3-decoration-none w3-inline w3-expand"
                            ref={input_el_ref}
                            value={search_text}
                            placeholder={placeholder || 'Search'}
                            onChange={(evt) => setSearchText(evt.target.value)}
                            onKeyUp={onTextInputKeyUp}
                            onClick={(evt) => !evt.target.value && showSuggestionsForSearchText("")}
                        />
                        {   
                            filtered_suggestions?.length && (input_el_ref.current === document.activeElement)
                            ?   <div style={{position: "absolute", top: "100%", minWidth: "200px", zIndex: 5, maxHeight: "300px", overflowY: "auto"}}
                                    className='w3-white w3-2px-shadow'
                                >
                                    <div className='w3-list w3-list-bordered'>
                                        {
                                            filtered_suggestions.map((v, i) =>
                                                <div className="w3-padding-8" 
                                                    key={i}
                                                    style={{backgroundColor: highlighted_suggestion === i ? "lightgrey" : "white"}}
                                                    onClick={(evt) => {addSelection(v)}}
                                                >
                                                    {v.title || v.view || v._id || v}
                                                    {v.description 
                                                        ?   <div className='w3-small w3-text-grey w3-padding-topbottom-4'>
                                                                {v.description}
                                                            </div> 
                                                        : null
                                                    }
                                                </div>
                                            )
                                        }
                                    </div>
                                </div>
                            :   search_text && options?.on_no_suggestions_text
                                ?   <div style={{position: "absolute", top: "100%", minWidth: "200px"}}
                                        className='w3-white w3-2px-shadow'
                                    >{options.on_no_suggestions_text}</div>
                                :   null
                        }
                    </div>
                :   null
            }
        </div>
    );
}

function SelectTags({tags, selected_tags:_selected_tags, onSelectedTagsUpdated, className, placeholder_text}){
    const [filtered_tags, setFilteredTags] = useState(tags);
    const [selected_tags, setSelectedTags] = useState(_selected_tags || []);
    const [search_text, setSearchText] = useState("");

    useEffect(() => {_selected_tags?.length && setSelectedTags(_selected_tags)}, [_selected_tags]);
    useEffect(
        () => {
            if(isFunction(tags)){
                tags(search_text).then((s) => {setFilteredTags(s);});
            } else {
                setFilteredTags(
                    tags?.filter(
                        (s) => {
                            let _s = s.title || s;
                            return _s.toLowerCase().includes(search_text.toLowerCase())
                        }
                    )
                );
            }

        }, [selected_tags, search_text]
    )
    const handleTagSelection = (tag) => {
        const new_selected_tags = selected_tags.includes(tag) 
            ? selected_tags.filter((t) => t !== tag)
            : [...selected_tags, tag];
        setSearchText("");
        setSelectedTags(new_selected_tags);
        onSelectedTagsUpdated && onSelectedTagsUpdated(new_selected_tags);
    };
    return (
        <div className={className}>
            {
                selected_tags.length > 0 
                ?   <div className='w3-flex-row-wrap w3-list-horizontal w3-margin-bottom-8'>
                        {   
                            selected_tags.map(
                                (tag, i) => 
                                    <div className='tw-flex tw-border tw-p-1 tw-rounded tw-items-center tag-tertiary' key={tag}>
                                        {tag} <span className='w3-large tw-cursor-pointer' onClick={() => handleTagSelection(tag)}>&times;</span>
                                    </div>
                            ) 
                        }
                    </div>
                :   null
            }
            <div className='w3-row w3-relative'>
                {
                    !tags.length || tags.length > 10
                    ?   <input type="text" className='input-primary w3-margin-bottom'
                            value={search_text}
                            onChange={(evt) => setSearchText(evt.target.value)}
                            placeholder={placeholder_text || 'Search tags'}
                        />
                    :   null
                }
                <div className='w3-list w3-scroll' style={{maxHeight: "200px"}}>
                    {
                        filtered_tags.map(
                            (tag, i) =>
                                <div className='w3-col s6' key={i}>
                                    <label className='tw-flex tw-gap-1 tw-items-center'>
                                            <input type="checkbox"
                                                checked={selected_tags.includes(tag)}
                                                onChange={() => handleTagSelection(tag)}
                                            className='input-primary' />
                                        <div>{tag}</div>
                                    </label>
                                </div>
                        )
                    }
                </div>
            </div>
        </div>
    )
}

function onServiceWorkerUpdateFound(registration) {
    if(!registration) return;
    registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
}

/* categories is list of string a > b > c */
function CategorySelectorStrip({categories, onCategoriesSelected}){
    const [category_tree, setCategoryTree] = useState({});
    const [selected_category_keys, setSelectedCategoryKeys] = useState([]);
    useEffect(
      () => {
        let category_tree = {};
        categories.forEach(
          (c) => setKeyPath(category_tree, c.split(/>|&gt;/).map((_c) => _c.trim().toLowerCase()))
        );
        setCategoryTree(category_tree);
      }, [categories]
    );
    const levels = [Object.keys(category_tree)]; // first level should be visible
    let _category_tree = category_tree;
    for(let i=0;i<selected_category_keys.length;i++){
      _category_tree = _category_tree[selected_category_keys[i]];
      _category_tree && levels.push(Object.keys(_category_tree));
    }
  
    const onCategorySelected = (category, level_index) => {
      let _selected_category_keys = selected_category_keys.slice(0, level_index)
      if(selected_category_keys[level_index] !== category){ // toggle
        _selected_category_keys.push(category);
      }
      setSelectedCategoryKeys(_selected_category_keys);
      onCategoriesSelected?.(_selected_category_keys);
    }
  
  
    return <div className='w3-padding-8 w3-flex-row-wrap w3-list-horizontal-16'>
      {
        levels.map(
            (l, i) => {
              return (
                <div className='w3-capitalize w3-flex w3-no-wrap w3-flex-vcenter w3-center w3-list-horizontal-16 w3-scroll-x w3-padding-bottom-8 w3-hide-scrollbar w3-animate-left'
                    key={i}
                    style={{
                        "borderLeft": i > 0 ? "1px solid black" : null,
                        "paddingLeft": i > 0 ? "8px" : null,
                    }}
                >
                  {
                    l.map(
                      (c) => (
                        <div className={`tw-px-4 tw-py-2 tw-rounded-full tw-text-sm ${selected_category_keys[i] === c ? "" : "tw-border tw-border-[#898989]"}`}
                        style={{
                            "backgroundColor": selected_category_keys[i] === c ? "#15966E" : null,
                            "color": selected_category_keys[i] === c ? "#FFFFFF" : "#898989" 
                        }}
                          key={c} onClick={() => onCategorySelected(c, i)}
                        >{c}</div>
                      )
                    )
                  }
                </div>
              );
            }
          )
        }
    </div>
}

function ListAndDictView({data}){
    if(Array.isArray(data)){
        return <table>
            <tbody>
                {data.map((d, i) => {
                    return  <tr key={i}>
                        <td><ListAndDictView data={d} /></td>
                    </tr>
                })}
            </tbody>
        </table>
    }
    else if(typeof data === "object"){
        return <table>
            <tbody>
                {Object.entries(data).map(([k, v]) => {
                    return <tr key={k}>
                        <td>{k}</td>
                        <td><ListAndDictView data={v} /></td>
                    </tr>
                })}
            </tbody>
        </table>
    } else {
        return data;    
    }
}

function AutoInputOrTextArea({value, defaultValue, ...props}){
    const is_large_text = (value || defaultValue || "").toString().length > 100
    if(is_large_text){
        if(value !== undefined){
            return <textarea {...props} value={value} />
        }
        return <textarea {...props} defaultValue={defaultValue} />
    }
    if(value !== undefined){
        return <input {...props} value={value} />
    }
    return <input {...props} defaultValue={defaultValue} />
}

var SCHEMA_DEFS = {};
function addSchemaDefs(defs){
    function parseDefsAndUpdateRefs(parent, path){
        let obj = getKeyPath(parent, path);
        if(!obj) return;
        if(Array.isArray(obj)){
            for(let i=0;i<obj.length;i++){
                parseDefsAndUpdateRefs(parent, [...path, i]);
            }
        }
        else if(typeof obj === "object"){
            for(let [key, value] of Object.entries(obj)){
                if(key === "$ref"){
                    let ref_key = value.split("/").pop();
                    setKeyPath(parent, path, getKeyPath(SCHEMA_DEFS, ref_key) || getKeyPath(parent, ref_key));
                }
                parseDefsAndUpdateRefs(parent, [...path, key]);
            }
        }
    }

    parseDefsAndUpdateRefs(defs, []);
    Object.assign(SCHEMA_DEFS, defs);
}

const schemaTypeToName = (type) => type == "array" ? "List" : type == "object" ? "Map" : type
const schemaTypeObject = (schema) => {return {"array": [], "object": {}}[schema?.type] || ""};
const schemaMatchScore = (schema, data) => {
    if(!data) return 1;
    if(!schema) return 0;
    let score = 0;
    if(typeof schema === "string") schema = {"type": schema}; // just in case, usually not needed

    if(schema.oneOf || schema.anyOf){
        /* pick max of scores */
        let maxScore = 0;
        for(let option of schema.oneOf || schema.anyOf){
            const optionScore = schemaMatchScore(option, data);
            if(optionScore > maxScore) maxScore = optionScore;
        }
        score += maxScore;
        return score;
    }
    if(!["string", "number", "boolean", "object", "array"].includes(schema.type)) return 1; // unknown type, assume matches

    if(schema.type === "string" && typeof data === "string") score += 50;
    else if(schema.type === "number" && typeof data === "number") score += 50;
    else if(schema.type === "boolean" && typeof data === "boolean") score += 50;
    else if(schema.type === "object" && typeof data === "object" && !Array.isArray(data)) score += 50;
    else if(schema.type === "array" && Array.isArray(data)) score += 50;
    if(schema.type === "object" && data && typeof data === "object"){
        for(let key of Object.keys(data)){
            if(schema.properties && schema.properties[key]){
                score += schemaMatchScore(schema.properties[key], data[key]);
            } else if(schema.additionalProperties){
                score += schemaMatchScore(schema.additionalProperties, data[key]);
            }
        }
    }
    if(schema.type === "array" && Array.isArray(data)){
        for(let item of data){
            if(schema.items){
                score += schemaMatchScore(schema.items, item);
            }
        }
    }

    return score;
}
var _schema_ukey_counter = 0;
function SchemaObjectCreator({schema, schema_path, data:_data_, data_path, onUpdate, customRenderer}){
    const [render_key, rerender] = useRerenderWithValue();
    
    const schema_ref = useRef(schema || {});
    schema = schema_ref.current;
    if(!schema._ukey) { // first time // root
        if(isString(schema)) schema = {"type": schema}
        schema = JSON.parse(JSON.stringify(schema));
        schema._ukey = ++_schema_ukey_counter;
        schema_ref.current = schema;
    }

    schema._data_ = schema._data_ || {};
    // schema._data_ = schema._data_ || {}; // usable ui fields, parallel to _data_
    /* correct any root data */
    if(!schemaMatchScore(schema, _data_)) _data_ = schemaTypeObject(schema);
    if(data_path?.length && !_data_) _data_ = data_path.find((x) => typeof x === "string") ? {}: [];
    const data_ref = useRef(_data_ || schemaTypeObject(schema));
    const data = data_ref.current;

    /* schema should be present */
    schema_path = schema_path || [];
    var _schema = getKeyPath(schema, schema_path);
    if(isString(_schema)) _schema = {"type": schema};
    
    /* _data: current data at the path, correct if required */
    data_path = data_path || [];
    const __data = getKeyPath(data, data_path);
    const _data = !schemaMatchScore(_schema, __data) ? schemaTypeObject(_schema) : __data;

    // console.log(
    //     "SchemaObjectCreator", "schema", schema, "\n", "schema_path", schema_path, "\n",
    //     "_schema", _schema, "\n", "data", data, "\n", "data_path", data_path, "\n", "_data", _data
    // );

    /* 
        this effect is to listen for rerender events on all components,
        sometimes useful when a child object updates a different object and you want to rerender
    */
    useEffect(
        () => {
            // top level
            if(!data_path?.length){
                schema.rerender = () => {
                    broadcaster.broadcast_event(`schema:object:rerender:${schema._ukey}`);
                    rerender();
                };
                return () => {delete schema.rerender;}
            } else {
                let _rerender = () => {rerender();};
                broadcaster.add_event_listener(`schema:object:rerender:${schema._ukey}`, _rerender);
                return () => {
                    broadcaster.remove_event_listener(`schema:object:rerender:${schema._ukey}`, _rerender);
                }
            }
        }, [schema]
    );

    if(!_schema) return null;   
    if(customRenderer){
        const custom_render = customRenderer(schema, schema_path, data, data_path, onUpdate)
        if(custom_render){
            return <>
                {_schema.description && <div>{_schema.description}</div>}
                {custom_render}
            </>
        }
    }
    if(_schema.type === 'object'){
        /* 
            _properties: added on fly 
            properties: defined in schema
            additionalProperties: schema for new keys
        */
        if(
            !_schema.properties 
            && (
                _schema.additionalProperties === undefined
                || _schema.additionalProperties === true
            )
        ){
            _schema.additionalProperties = {"type": "string"}; // default
        }
        if(_schema.additionalProperties){
            if(!_schema._properties){
                _schema._properties = {};
                _schema.properties && Object.assign(_schema._properties, _schema.properties);
            }
            for(let key of Object.keys(_data || {})){
                if(!_schema._properties[key]) _schema._properties[key] = _schema.additionalProperties;
            }
        } else {
            _schema._properties = _schema.properties || {};
        }

        const click_to_add_optional_fields_path = [...data_path, '_click_to_add_optional_fields'];
        let click_to_add_optional_fields = getKeyPath(schema._data_, click_to_add_optional_fields_path);
        if(!(click_to_add_optional_fields instanceof Set)){
            /* This shows the list as pills to add optional fields */
            click_to_add_optional_fields = setKeyPath(
                schema._data_, click_to_add_optional_fields_path, new Set()
            );
            for(let key of Object.keys(_schema._properties)){
                if(_data && _data[key] !== undefined) continue;
                if(!_schema.required?.includes(key)){
                    click_to_add_optional_fields.add(key);
                }
            }
        }
        for(let key of Object.keys(_data || {})){
            click_to_add_optional_fields.delete(key);
        }
        
        /* for additional properties */
        const addAdditionalProperty = (key, value) => {
            if(!key || _schema.properties?.[key]) return;
            _schema._properties[key] = _schema.additionalProperties;
            if(value !== undefined){
                if(!data) data_ref.current = {};
                setKeyPath(data_ref.current, [...data_path, key], value);
            }
            rerender();
            onUpdate?.(data_ref.current);
        }

        const deleteAdditionalProperty = (key) => {
            delete _schema._properties[key];
            delete _data[key];
            rerender();
            onUpdate?.(data);
        }

        const clearOptionalField = (key) => {
            _data && delete _data[key];
            click_to_add_optional_fields.add(key);
            rerender();
            onUpdate?.(data);
        }

        var _to_add_keys = getKeyPath(schema._data_, [...data_path, '_to_add_keys']);
        return <>
            {
                _schema.description 
                && <div className='w3-small w3-text-grey w3-padding-topbottom-8'>
                    {_schema.description}
                </div>
            }
            <table className='w3-table-all w3-expand-x tw-my-4'>
                <tbody>
                    {
                        Object.entries(_schema._properties).map(([key, value]) => (
                           (
                            !click_to_add_optional_fields.has(key)
                            && (
                                (_data?.[key] !== undefined && _data?.[key] !== null ) // defined
                                || _schema.properties?.[key]
                            ) 
                          ) &&  <tr key={key}>
                                <td className='tw-w-[30%]'>
                                    <div>
                                        {key}
                                        {
                                            _schema.required && _schema.required.includes(key)
                                            ?   <span className='w3-text-red'>*</span>
                                            :   _schema.properties?.[key] // existing property in definition
                                                ?   <span className='w3-text-red tw-ml-1'
                                                        onClick={() => clearOptionalField(key)}
                                                    >x</span>
                                                :   null
                                        }
                                    </div>
                                    {value?.title && <div className='w3-text-grey tw-text-xs'>{value.title}</div>}
                                </td>
                                <td>
                                    <SchemaObjectCreator
                                        schema={schema}
                                        schema_path={[...schema_path, "_properties", key]}
                                        data={data}
                                        data_path={[...data_path, key]}
                                        onUpdate={onUpdate}
                                        customRenderer={customRenderer}
                                    />
                                </td>
                                {
                                    !_schema.properties?.[key]  // custom property added on flight
                                    ?   <td><TrashIcon
                                            onClick={() => deleteAdditionalProperty(key)}
                                            size={18}
                                        /></td>
                                    : null
                                }
                            </tr>
                        ))
                    }
                    {
                        /* add new keys */
                        _to_add_keys?.map(
                            (item, i) => {
                                return <tr key={i}>
                                    <td>
                                        <input type="text" className='w3-input w3-border'
                                            defaultValue={item.key}
                                            onChange={(evt) => item.key = evt.target.value}
                                            placeholder='key'
                                        />
                                    </td>
                                    <td>
                                        <SchemaObjectCreator
                                            schema={_schema.additionalProperties}
                                            data={item.data}
                                            onUpdate={(data) => {
                                                item.data = data;
                                            }}
                                        />
                                    </td>
                                    <td>
                                        <div className="tw-flex tw-gap-2">
                                            <CircleCheckIcon onClick={() => {
                                                addAdditionalProperty(item.key, item.data);
                                                _to_add_keys.splice(i, 1);
                                            }} className="tw-text-red-500" />
                                            <XIcon onClick={() => {
                                                _to_add_keys.splice(i, 1);
                                                rerender();
                                            }} />
                                        </div>
                                    </td>
                                </tr>
                            }
                        )
                    }
               </tbody>
            </table>
            {   
                /* add additional properties */
                click_to_add_optional_fields.size
                ?  <div className='w3-flex w3-array-list-8 w3-flex-row-wrap tw-my-2'> 
                    {
                        [...click_to_add_optional_fields].map(
                            (key) => {
                                return <div className='tag-tertiary tw-bg-white tw-capitalize tw-border tw-border-[#D1D1D1]' onClick={
                                        () => {
                                            click_to_add_optional_fields.delete(key);
                                            setKeyPath(
                                                data, 
                                                [...data_path, key], schemaTypeObject(_schema._properties[key])
                                            );
                                            onUpdate?.(data);   
                                            rerender();
                                        }
                                    }
                                    key={key}
                                >+ {key.replaceAll("_", " ")}</div>
                            })
                    }
                    </div>
                :   null
            }
            {
                _schema.additionalProperties 
                &&  <div className='w3-button w3-black w3-small tw-my-2'
                        onClick={() => {
                            (
                                _to_add_keys || setKeyPath(schema._data_, [...data_path, '_to_add_keys'], [])
                            ).push({});
                            rerender()
                        }}
                    >Add</div>
            }
        </>
    }
    else if(_schema.type === 'array'){
        _schema.items = _schema.items || {"type": "string"}; // default
        if(_data?.length === 0 && !_data.initial_dummy) {
            _data.push(schemaTypeObject(_schema.items));
            _data.initial_dummy = true; // to enable delete, but just show default on first time
        }
        return <>
            {
                _schema.description && 
                <div className='w3-small w3-text-grey w3-padding-topbottom-8'>
                    {_schema.description}
                </div>
            }
            <table className='tw-w-full'>
                <tbody>
                    {
                        _data?.map((d, i) => (
                            <tr key={_data.length + "" + i}>
                                <td>
                                    <SchemaObjectCreator
                                        schema={schema}
                                        schema_path={[
                                            ...schema_path, "items", 
                                            ...(d?.__ui_next_selected_type_i || [])
                                        ]}
                                        data={data}
                                        data_path={[...data_path, i]}
                                        onUpdate={onUpdate}
                                        customRenderer={customRenderer}
                                    />
                                </td>
                                <td>
                                    <TrashIcon
                                        onClick={() => {
                                            _data.splice(i, 1);
                                            _schema.items.__data_item_types?.[i] 
                                                && _schema.items.__data_item_types.splice(i, 1);
                                            rerender();
                                            onUpdate?.(data);
                                        }}
                                        size={18}
                                    />
                                </td>
                            </tr>
                        ))
                    }
                </tbody>
            </table>
            <div className='w3-list-horizontal-16 w3-flex'>
                {
                    // select type for new array item if anyOf or oneOf
                    ["anyOf", "oneOf"].map((_type) => {
                        return _schema.items[_type]?.length
                            ?   <select onChange={(evt) => {
                                    _schema.__ui_next_selected_type_i = evt.target.value 
                                        ? [_type, parseInt(evt.target.value)]
                                        : undefined;
                                    }}
                                    key={_type}
                                    className='w3-input w3-border tw-mr-2'
                                >
                                    <option value="">Select Type</option>
                                    {
                                        _schema.items[_type].map((option, index) => (
                                            <option key={index} value={index}>
                                                {option.title || option.description || option.type}
                                            </option>
                                        ))
                                    }
                                </select>
                            :   null
                    })
                }
                <button className="btn-secondary tw-my-2"
                    onClick={
                        () => {
                            if(
                                (_schema.items.oneOf || _schema.items.anyOf)
                                && !_schema.__ui_next_selected_type_i
                            ) return;
                            let _next_schema = _schema.__ui_next_selected_type_i
                                ? getKeyPath(_schema.items, _schema.__ui_next_selected_type_i)
                                : _schema.items;
                            let __data = schemaTypeObject(_next_schema);
                            if(typeof __data === "object"){ /* each item can have type */
                                __data.__ui_next_selected_type_i = _schema.__ui_next_selected_type_i;
                            }
                            (_data || setKeyPath(data, data_path, [])).push(__data);
                            onUpdate?.(data);
                            rerender();
                        }
                    }
                >Add</button>
            </div>
        </>
    }
    else if(_schema.anyOf || _schema.oneOf){
        let _types = _schema.anyOf || _schema.oneOf;
        let _data_schema_index = null;
        if(_schema.selected_one_of !== undefined){
            _data_schema_index = _schema.selected_one_of;
        } else if(_data){
            /* need to infer best matching schema */
            let scores = _types.map((__schema, index) => {return [index, schemaMatchScore(__schema, _data)]});
            scores.sort((a, b) => b[1] - a[1]);
            _data_schema_index = scores[0][0]; // best match
        }
        return (
            <div>
                {
                   _data_schema_index === null
                    ? <fieldset className='tw-flex tw-flex-row tw-space-x-2 tw-mb-2'>
                        {
                            _types.map((__schema, index) => {
                                return <label key={index} className='tw-items-center tw-flex tw-flex-row tw-gap-x-2'>
                                    <input type="radio" name="type" value={index} 
                                        className='w3-radio'
                                        onChange={(evt) => {
                                            _schema.selected_one_of = index; // track so we know 
                                            if(!data_path?.length) data_ref.current = schemaTypeObject(__schema);
                                            else setKeyPath(data_ref.current, data_path, schemaTypeObject(__schema));
                                            rerender();
                                            onUpdate?.(data_ref.current);
                                        }}
                                    />
                                    {__schema.description || __schema.name || __schema.title || schemaTypeToName(__schema.type) || index}
                                </label>
                            })
                        }
                        </fieldset>
                    : null
                }
                {
                    (data_path?.length > 0 || _data_schema_index !== null)  // need to select a type first if root
                    && <SchemaObjectCreator
                        schema={schema}
                        schema_path={[...schema_path, _schema.anyOf ? "anyOf" : "oneOf", _data_schema_index || 0]}
                        data={data}
                        data_path={data_path}
                        onUpdate={onUpdate}
                        customRenderer={customRenderer}
                    />
                }
            </div>
        );
    }
    else if(!_schema.type || _schema.type === "string"){
        let val = _data || _schema.default || "";
        let placeholder = _schema.placeholder;
        if(!isString(val)) val = JSON.stringify(val, null, 2); // object -> string display
        const onChange = (evt) => {
            let _val = evt.target.value;
            // if date converver to yyyy-mm-dd HH:MM
            if(_schema.format === "date-time") _val = new Date(_val).toISOString().replace("T", " ").split(".")[0];
            if(data_path?.length){
                setKeyPath(data, data_path, _val);
                onUpdate?.(data);
            } else {
                onUpdate?.(_val);
            }
        }
        return <div style={{minWidth: "100px"}}>
            {_schema.description && <div className='w3-small w3-text-grey w3-padding-topbottom-8'>{_schema.description}</div>}
            {
                _schema.enum
                ?   <select onChange={onChange}
                        defaultValue={val}
                        className='w3-input w3-border'
                    >
                        <option value="">Select</option>
                        {
                            _schema.enum.map((option, index) => (
                                <option key={index} value={option}>{option}</option>
                            ))
                        }
                    </select>
                :   ((_schema.maxLength && _schema.maxLength > 75) || val.length > 75)
                    ?   <textarea
                            className='w3-input w3-border'
                            defaultValue={val} onChange={onChange}
                            key={render_key}
                            placeholder={placeholder || "enter a value"}
                            style={{minHeight: "200px"}}
                        />
                    :   <input type={
                                _schema.format === "date-time" 
                                ? "datetime-local" 
                                : _schema.format === "date" 
                                    ? "date" : "text"
                            }
                            key={render_key}
                            defaultValue={val} onChange={onChange}
                            className='w3-input'
                            placeholder={placeholder || "enter a value"}
                        />
            }
        </div>
    } 
    else if(_schema.type === "boolean"){
        return <>
            {_schema.description && <div className='w3-small w3-text-grey w3-padding-topbottom-8'>{_schema.description}</div>}
            <input type="checkbox" 
                defaultChecked={_data !== undefined ? _data : _schema.default}
                onChange={(evt) => {
                    let _val = evt.target.checked;
                    if(data_path?.length){
                        setKeyPath(data, data_path, _val);
                        onUpdate?.(data);
                    } else {
                        onUpdate?.(_val);
                    }
                }}
            />
        </>
    }
    else {
        let val = _data || _schema.default;
        return <>
            {_schema.description && <div className='w3-small w3-text-grey  w3-padding-topbottom-8'>{_schema.description}</div>}
            <input type={"text"} 
                defaultValue={val}
                key={render_key}
                className='w3-input'
                onChange={(evt) => {
                    let val = evt.target.value;
                    let _val = parseFloat(val);
                    if(!isNaN(_val)) val = _val; // convert to number if possible
                    if(data_path?.length){
                        setKeyPath(data, data_path, val);
                        onUpdate?.(data);
                    } else {
                        onUpdate?.(val);
                    }
                }} 
                placeholder={"enter a number"}
            />
        </>
    }
}

function TextAreaWithCheckBox({title, defaultValue, onChange, className, placeholder, input_type}){
    const [show, setShow] = useState(isString(defaultValue));
    const [value, setValue] = useState(defaultValue || "");
    const ctx = useRef({"prev_value": show ? value : null}).current;
    useEffect(
        () => {
            const new_value = show ? value : null;
            ctx.prev_value !== new_value && onChange(new_value); // only call when value changes
            ctx.prev_value = new_value;
        }, [show, value]
    );

    return (
        <div className="w3-list">
            <label className="w3-flex w3-flex-vcenter">
                <input className="w3-check" type="checkbox"
                    onChange={(evt) => setShow(evt.target.checked)} 
                    checked={show}
                />
                <div className="w3-margin-left">{title}</div>
            </label>
            {
                show 
                ?   !input_type
                    ?   <input type="text" defaultValue={defaultValue}
                            className={className || "w3-input"}
                            placeholder={placeholder}
                            onChange={(evt) => setValue(evt.target.value)}
                        />
                    :   <textarea defaultValue={defaultValue}
                            className={className || "w3-input"}
                            placeholder={placeholder}
                            onChange={(evt) => setValue(evt.target.value)}
                        />
                :   null
            }
        </div>
    );
}

function InfoText({text, className = "", img_className = "", icon_color}){
    const info_icon_color = icon_color || "#000000"
    return <span 
        onClick={(evt) => {
            evt.stopPropagation();
            Popup.showContextMenu(
              evt.currentTarget,
              <div
                className={`tw-p-2 tw-text-xs ${className}`}
                style={{ maxWidth: "400px", overflow: "auto", maxHeight: "400px", wordBreak: "break-word" }}
              >
                {text}
              </div>
            );
        }}
    >
        <InfoIcon
            className={`tw-text-inherit tw-inline-block tw-w-[18px] ${img_className}`}
            stroke={info_icon_color} strokeWidth={0.5}
        />
    </span>
}

function EditableTextArea({text, placeholder, onUpdate}){
    const [is_editing, setIsEditing] = useState(false);
    const ctx = useRef({"edited_text": text}).current;
    const doUpdateText = async (evt) => {
        setIsEditing(false);
        if(ctx.edited_text === text) return;
        try{await onUpdate(ctx.edited_text)}
        catch(e){setIsEditing(true);}
    }

    return (
        <div className='tw-flex tw-flex-row tw-gap-2'>
            {
                is_editing
                ?   <>
                        <textarea defaultValue={text} 
                            onChange={(evt) => ctx.edited_text = evt.target.value}
                            className='w3-input' placeholder={placeholder}  
                        />
                        <CircleCheckIcon size={22} className="tw-mt-1" onClick={doUpdateText} />
                    </>
                :   <div className="tw-gap-2 tw-flex tw-flex-row" onClick={() => setIsEditing(true)}>
                        {text || <span className='tw-text-gray-300'>{placeholder}</span>}
                    </div>
            }
        </div>
    )
}

function SimpleGrid({children, L=1200, M=800, S=400}){
    const container_el = useRef(null);
    const ViewPort = useBroadcastedState("viewport")[0];
    const [cols, setCols] = useState([]);
    useEffect(
        () => {
            let width = container_el.current?.offsetWidth || 0;
            let num_cols = width >= L ? 3 : width >= M ? 2 : 1;
            const cols = [];
            for(let i=0;i<num_cols;i++){
               cols.push([]);
            }
            for(let i=0;i<children.length;i++){
                cols[i % num_cols].push(children[i]);
            }
            setCols(cols);    
        }, [ViewPort.WIDTH, children.length]
    );

    return (
        <div ref={container_el} className='w3-flex w3-flex-row'>
            {
                cols.map(
                    (col, i) => {
                        return <div key={i} className='w3-list w3-padding-4' style={{width: `${100/cols.length}%`}}>
                            {col}
                        </div>
                    }
                )
            }
        </div>
    );
}

function SimpleMDE({value, onChange, className}){    
    const el = useRef(null);
    useEffect(() => {
        if(!el.current) return;
        Promise.all([
            loadCss("https://cdn.jsdelivr.net/npm/easymde/dist/easymde.min.css"),
            loadScript("https://cdn.jsdelivr.net/npm/easymde/dist/easymde.min.js")
        ]).then(() => {
            if(!el.current || el.current.is_initialized) return;
            el.current.is_initialized = true;
            const easy_mde = new window.EasyMDE({element: el.current});
            easy_mde.value(value);
            easy_mde.codemirror.on("change", () => {
                onChange?.(easy_mde.value());
            });
        });
    }, [el.current]);
    return <textarea className={className} ref={el} defaultValue={value} />
}


function CodeViewer({value, className}){
    const code = value == null ? "" : String(value);
    const is_python = /^\s*(def|class|from|import|elif|except|with|async\s+def)\b/m.test(code) || /^\s*#/m.test(code);
    const keywords = new Set(
        (is_python
            ? "and as assert async await break class continue def del elif else except False finally for from global if import in is lambda None nonlocal not or pass raise return True try while with yield"
            : "async await break case catch class const continue debugger default delete do else export extends false finally for from function if import in instanceof let new null return static super switch this throw true try typeof undefined var void while with yield"
        ).split(" ")
    );
    const token_re = is_python
        ? /("""[\s\S]*?"""|'''[\s\S]*?'''|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|#[^\n]*|\b\d+(?:\.\d+)?\b|\b[A-Za-z_]\w*\b)/g
        : /(\/\*[\s\S]*?\*\/|\/\/[^\n]*|`(?:\\.|[^`\\])*`|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|\b\d+(?:\.\d+)?\b|\b[A-Za-z_$][\w$]*\b)/g;
    const colors = {
        comment: "#6a737d",
        string: "#032f62",
        number: "#005cc5",
        keyword: "#d73a49"
    };
    const parts = [];
    let last_index = 0;
    let match;
    while((match = token_re.exec(code))){
        const text = match[0];
        if(match.index > last_index) parts.push(code.slice(last_index, match.index));
        let type = null;
        if(text.startsWith("#") || text.startsWith("//") || text.startsWith("/*")) type = "comment";
        else if(text[0] === "\"" || text[0] === "'" || text[0] === "`") type = "string";
        else if(/^\d/.test(text)) type = "number";
        else if(keywords.has(text)) type = "keyword";
        parts.push(type ? <span key={parts.length} style={{color: colors[type]}}>{text}</span> : text);
        last_index = token_re.lastIndex;
    }
    if(last_index < code.length) parts.push(code.slice(last_index));

    return <pre className={className} style={{
        background: "#f6f8fa",
        border: "1px solid #d0d7de",
        borderRadius: "6px",
        color: "#24292f",
        fontFamily: "ui-monospace, SFMono-Regular, SFMono, Consolas, Liberation Mono, Menlo, monospace",
        fontSize: "13px",
        lineHeight: "1.45",
        margin: 0,
        overflow: "auto",
        padding: "12px",
        tabSize: 4,
        whiteSpace: "pre"
    }}><code>{parts}</code></pre>
}

function detectCodeMirrorMode(code){
    code = code || "";
    if(/^\s*(<!doctype html|<html\b|<[a-z][\w:-]*(\s|>|\/>))/im.test(code)) return "htmlmixed";
    if(/^\s*(def|class|from|import|elif|except|with|async\s+def)\b/m.test(code) || /^\s*#.*$/m.test(code)) return "python";
    return "javascript";
}

function CodeEditor({value, onChange, className, placeholder}){
    const el = useRef(null);
    const editor = useRef(null);
    useEffect(() => {
        if(!el.current || editor.current) return;
        Promise.all([
            loadCss("https://cdn.jsdelivr.net/npm/codemirror@5.65.16/lib/codemirror.min.css"),
            loadScript("https://cdn.jsdelivr.net/npm/codemirror@5.65.16/lib/codemirror.min.js")
        ]).then(() => Promise.all([
            loadScript("https://cdn.jsdelivr.net/npm/codemirror@5.65.16/mode/javascript/javascript.min.js"),
            loadScript("https://cdn.jsdelivr.net/npm/codemirror@5.65.16/mode/python/python.min.js"),
            loadScript("https://cdn.jsdelivr.net/npm/codemirror@5.65.16/mode/xml/xml.min.js"),
            loadScript("https://cdn.jsdelivr.net/npm/codemirror@5.65.16/mode/css/css.min.js"),
            loadScript("https://cdn.jsdelivr.net/npm/codemirror@5.65.16/mode/htmlmixed/htmlmixed.min.js")
        ]).then(() => {
            if(!el.current || editor.current || !window.CodeMirror) return;
            editor.current = window.CodeMirror.fromTextArea(el.current, {
                mode: detectCodeMirrorMode(value),
                lineNumbers: true,
                lineWrapping: true,
                indentUnit: 4,
                tabSize: 4,
                viewportMargin: Infinity,
                extraKeys: {
                    Tab: (cm) => {
                        if(cm.somethingSelected()){
                            cm.indentSelection("add");
                            return;
                        }
                        cm.replaceSelection(" ".repeat(cm.getOption("indentUnit")));
                    }
                }
            });
            editor.current.setValue(value || "");
            editor.current.on("change", (cm) => {
                cm.setOption("mode", detectCodeMirrorMode(cm.getValue()));
                onChange?.(cm.getValue());
            });
            if(className) editor.current.getWrapperElement().className += ` ${className}`;
            if(placeholder) editor.current.getInputField().setAttribute("placeholder", placeholder);
        }));
        return () => {
            if(!editor.current) return;
            editor.current.toTextArea();
            editor.current = null;
        };
    }, []);
    useEffect(() => {
        if(!editor.current || editor.current.getValue() === (value || "")) return;
        editor.current.setValue(value || "");
        editor.current.setOption("mode", detectCodeMirrorMode(value));
    }, [value]);
    return <textarea className={className} ref={el} defaultValue={value || ""} placeholder={placeholder} />
}


function ListOfItemsView({items}){

	const [keys, setKeys] = useState(null);
	const [headers, setHeaders] = useState(null);
	useEffect(() => {
		if(!items) return;
		let keys = ["title", "qty", "price_str"];
		let _keys = Object.keys(items?.[0] || {});
		setHeaders(_keys.map(toTitleCase));
		_keys.forEach((key) => {
			if (!keys.includes(key)) keys.push(key);
		});
		setKeys(keys);
		}, [items]
	);

	if(!headers) return null;
	
	return <table className="w3-table-all">
		{	
			headers 
			?	<thead>
				<tr className="w3-light-grey">
				{
					headers.map((header, i) => <th key={i}>{header}</th>)
				}
				</tr>
			</thead> 
			: null
		}
		<tbody>
		{
			items?.map((item, i) => 
				<tr key={i}>
					{
						keys.map((key, j) => <td key={j}>
							{
								!isObject(item[key]) 
								? item[key] : JSON.stringify(item[key])
							}
							</td>
						)
					}
				</tr>
			)
		}
		</tbody>
		</table>
  
}

function SimpleTableEditor({cols: _cols, rows: _rows, onRowsUpdated: _onRowsUpdated}){
    const [cols, setCols] = useState(null);
    const [rows, setRows] = useState(null);
    const ctx = useRef({}).current;
    useEffect(
        () => {
            let cols = (_cols || []).map(c => ({...c})); // make a copy
            let rows = (_rows || []).map(r => ({...r})); // make a copy
            let col_keys = new Set(cols.map(c => c.key));
            for(let row of rows){
                for(let k of Object.keys(row)){
                    if(!col_keys.has(k)){
                        cols.push({
                            "key": k,
                            "title": idToTitle(k),
                        });
                        col_keys.add(k);
                    }
                }
            }
            setCols(cols);
            setRows(rows);
        }, 
        [_cols, _rows]
    );

    const onRowsUpdated = () => {
        setRows([...rows]);
        _onRowsUpdated?.(
            rows.map(r => {
                let ret = {};
                for(let c of cols){
                    ret[c.key] = r[c.key];
                }
                return ret;
            }).filter(r => !!Object.values(r).filter(v => !!v).length ) // remove empty rows
        );
    }
    const addNewColumn = () => {
        Popup.open(
            "Add New Column",
            <div className='w3-list w3-padding-8'>
                <label>Column Key</label>
                <input type="text" className='w3-input'
                    onChange={(evt) => {
                        ctx.add_col_key = sanitizeToUnderscoreId(evt.target.value);
                    }}
                />
            </div>,
            {
                "ok_button": "Add", 
                "cb": (is_add) => {
                    is_add && ctx.add_col_key && setCols([...cols, {
                        "key": ctx.add_col_key,
                        "title": idToTitle(ctx.add_col_key),
                    }])
                }
            }
        );    
    }
    if(cols === null || rows == null) return null;
    return <div>
        <div
            className="tw-mb-4 tw-text-gray-600 tw-text-sm"
        >
            <button className="btn-secondary tw-mr-4"
                onClick={addNewColumn}
            >Add Col</button>            
            <button
                className="btn-primary-v2" 
                onClick={() => {
                    rows.push({ "__is_new": true, "__is_editing": true });
                    setRows([...rows]);
                }}
            >
                    Add Row
            </button>
        </div>
        <EditableTable 
            cols={cols} 
            rows={rows} 
            callbacks={{
                "onUpdate": async (row, updates) => {
                    let ret = {...updates};
                    for(let c of cols){
                        if(ret.hasOwnProperty(c.key)) continue;
                        ret[c.key] = row[c.key];
                    };
                    rows.splice(rows.indexOf(row), 1, ret);
                    return [ret, null];
                },
                "afterUpdate": (row) => {
                    onRowsUpdated();
                },
                "onReorder": async (to_position, from_position) => {
                    if(to_position === from_position) return;
                    const row = rows.splice(from_position, 1)[0];
                    rows.splice(to_position, 0, row);
                    onRowsUpdated();
                },
                "onDelete": async (row) => {
                    const index = rows.indexOf(row);
                    if(index > -1) rows.splice(index, 1);
                    onRowsUpdated();
                }
            }}
            L={0}
        />
    </div>;
}


function MarkedView({text, className, style}){
    const el = useRef();
    useEffect(() => {
        if(!text.includes("\n") || !el.current) return;
        loadScript("https://cdn.jsdelivr.net/npm/marked/marked.min.js").then(() => {
            if(el.current.is_initialized) return;
            el.current.is_initialized = true;
            el.current.innerHTML = window.marked.parse(text);
        });
    }, [el.current]);
    return <div className={className} ref={el} style={style}>{text}</div>
}

/* 
    JsonErrorTextView 
    @arguments: defaultValue, className, placeholder, onChange
    - Should render the textarea with a red border if the JSON is invalid
    - debounce using useEffect, indent to a proper json if it's valid
    - onChange should be called with the indented JSON object
*/
function JsonErrorTextView({defaultValue, className, placeholder, onChange, rows = "2"}){
    const [value, setValue] = useState(defaultValue || "");
    const [error, setError] = useState(null);

    useEffect(
        () => {
            const timer = setTimeout(() => {
                let _value = value || "";
                try {
                    _value = JSON.stringify(JSON.parse(value), null, 2);
                    setValue(_value);
                    setError(null); // <-- Reset error when JSON is valid
                } catch (e) {
                    setError(e.message);
                }
                onChange?.(value);
            }, 500);
            return () => {clearTimeout(timer);};
        }, [value]
    );

    return <>
        {error && value && <div className='w3-text-red w3-small'>{error}</div>}
        <textarea
            className={`${className} ${error ? "tw-border tw-border-red-500" : ""}`}
            placeholder={placeholder}
            value={value}
            onChange={(evt) => setValue(evt.target.value)}
            rows={rows}
        />
    </>;
}



function CountryPhoneCodeSelect({default_phone_code, default_country_code, onChange}){
    const [is_focused, setIsFocused] = useState(false);
    const [search_text, setSearchText] = useState();
    const ctx = useRef({}).current;
    const [selected_country, setSelectedCountry] = useState();
    const [filtered_suggestions, setFilteredSuggestions] = useState(COUNTRY_PHONE_CODES_LIST);
    useEffect(
        () => {
            if(!default_country_code && !default_phone_code) return;
            if(default_country_code){
                setSelectedCountry(
                    COUNTRY_PHONE_CODES_LIST.find((obj) => obj.code === default_country_code)
                );
            } else {
                setSelectedCountry(
                    COUNTRY_PHONE_CODES_LIST.find((obj) => obj.dial_code === default_phone_code)
                );
            }
        }, [default_phone_code]
    );

    const handleKeyUp = (evt) => {
        // if it's a alphanumeric key dobounce and clear the search_text
        if(/^[a-zA-Z0-9\+]$/.test(evt.key)) setSearchText((search_text || "") + evt.key.toLowerCase());
        if(ctx.timer) clearTimeout(ctx.timer);
        ctx.timer = setTimeout(() => {setSearchText(undefined);}, 500);
    }

    useEffect(
        () => {
            if(search_text === undefined) return;
            const _filtered_suggestions = COUNTRY_PHONE_CODES_LIST.filter((obj) => {
                return obj.name.toLowerCase().includes(search_text.toLowerCase())
                    || obj.dial_code.toLowerCase().includes(search_text.toLowerCase())
                    || obj.code.toLowerCase().includes(search_text.toLowerCase())
            })
            if(len(_filtered_suggestions) > 1) setFilteredSuggestions(_filtered_suggestions);
            else if(len(_filtered_suggestions) === 1){
                setSelectedCountry(_filtered_suggestions[0]);
            } else {
                setFilteredSuggestions(COUNTRY_PHONE_CODES_LIST);
            }
        }, [search_text]
    );

    useEffect(() => {
        if(!selected_country) return;
        ctx.el.blur();
        onChange?.(selected_country.dial_code.replace("+", ""), selected_country);
    }, [selected_country]);

    const doOnChange = (evt) => {
        setSelectedCountry(COUNTRY_PHONE_CODES_LIST.find((obj) => obj.name === evt.target.value));
    }

    return <div style={{"overflow": "visible", "height": "32px", "zIndex": 5}}
    >
        <select
            className={
                `tw-outline-none tw-text-xs tw-py-2 tw-mr-1 tw-w-[136px] tw-overflow-y-scroll
                    ${is_focused ? "tw-border tw-border-primary" : ""}`
            }
            style={{"maxHeight": "250px"}}
            value={selected_country?.name}
            onChange={doOnChange}
            ref={(el) => ctx.el = el}
            size={is_focused ? Math.max(1, len(filtered_suggestions)): 1}
            onFocus={() => setIsFocused(true)}
            onBlur={() => {setIsFocused(false); setSearchText("");}}
            onKeyUp={handleKeyUp}
        >
            {filtered_suggestions.map((obj) => (
                <option key={obj.code} value={obj.name}>{`${obj.flag} ${obj.name} ${obj.dial_code}`}</option>
            ))}
        </select>
    </div>
}


function LocalStorageBackedInput({storage_key, default_value, onChange, ...rest}){
    const [value, setValue] = useState(() => {
        const stored_value = localStorage.getItem(storage_key);
        return stored_value !== null ? stored_value : default_value || "";
    });

    const handleChange = (evt) => {
        const new_value = evt.target.value;
        setValue(new_value);
        localStorage.setItem(storage_key, new_value);
        onChange?.(new_value);
    };

    return <input value={value} onChange={handleChange} {...rest} />;
}


const newLineToParts = (str) => str.split("\n").map((line, i) => <React.Fragment key={i}>{line}<br/></React.Fragment>);

function textToParts(str){
	let s = 0;
	let parts = []; // we will flatMap it at the end, can contain list of lists
	while(s < str.length){
		/* find one of these characters 
		\n -> add to parts [s, foundIndex]
		""" or ''' or ``` -> add to parts [s, foundIndex] and skip to next triple character. that add <pre>newLineToParts(str.substring(s, foundIndex))</pre>

		update s to foundIndex + 1 and continue
		*/
		let found_index = -1;
		let c = "";
		for(c of ["\"\"\"", "'''", "```", "\n"]){ // order is important
			found_index = str.indexOf(c, s);
			if(found_index !== -1) break; 
		}
		if(found_index === -1){parts.push(str.substring(s)); break;} // if not found, take the rest of the string

		if(c === "\n") parts.push(str.substring(s, found_index), <br />);
		else if(c === "\"\"\"" || c === "'''" || c === "```"){
			parts.push(...newLineToParts(str.substring(s, found_index))); // add the part before the triple quotes
			let next_index = str.indexOf(c, found_index + c.length);
			if(next_index === -1) next_index = str.length; // if not found, take the rest of the string
			parts.push(
				<pre key={s} style={{
					"margin": "0", 
					"backgroundColor": "rgba(255, 0, 115, 0.05)",
					"whiteSpace": "pre-wrap",
					"wordBreak": "break-word",
					"overflowX": "auto"
				}}>
					{newLineToParts(str.substring(found_index + c.length, next_index).trim())}
				</pre>
			);
			found_index = next_index; // move to next character after the triple quotes
		}
		s = found_index + c.length; // move to next character after the found index
	}
	return parts;
}

function objToEl(obj, indent = 0, path = [], customRenderer){
	if(customRenderer){
		let render = customRenderer(obj, indent, path);
		if(render !== undefined) return render;
	}
	if(obj === null || obj === undefined){
		return <div style={{marginLeft: indent + "em"}}></div>
	}
	else if (Array.isArray(obj)) {
		return <div style={{marginLeft: indent + "em"}}>
			{
				obj.map((item, i) => 
					<React.Fragment key={i} >
						{objToEl(item, indent, [...path, i], customRenderer)}
					</React.Fragment>
				)
			}
		</div>
	} else if(typeof obj === "object"){
		return <table className="table-primary tw-mt-4 tw-mb-8">
			<tbody className="w3-bounded">
			{
				Object.entries(obj).map(([key, value], i) => 
					<tr key={i}>
						<td className="w3-text-grey tw-break-normal">{key}</td>
						<td>{objToEl(value, indent + 1, [...path, key], customRenderer)}</td>
					</tr>
				)
			}
			</tbody>
		</table>
	} 
	else if(typeof obj === "boolean"){
		return <div style={{marginLeft: indent + "em"}}>{obj ? "True" : "False"}</div>
	}
	else {
		/* format \n with line breaks */
        let full_txt = obj.toString();
		let parts = textToParts(full_txt);
		return <div style={{
			"marginLeft": indent + "em",
		}}
			onClick={
				parts.length < 4 
				? null 
				: () => {
					Popup.show(
						"", <div style={{
							"maxHeight": "80vh", "overflowY": "auto", 
							"padding": "2em 2em"
						}}>
							{parts.map((part, i) => <React.Fragment key={i}>{part}</React.Fragment>)}
						</div>
					);
				}
			}
		>
			<div style={{
				"maxHeight": "100px",
				"overflowY": "auto",
			}}>
				{parts.slice(0, 4).map((part, i) => <React.Fragment key={i}>{part}</React.Fragment>)}
			</div>
			{
				parts.length > 4 &&
				<div className="tw-text-blue-500">View More</div>
			}
		</div>
	}
}

export {
    ShortImageAndDescription, SimpleTableInput, TableInput, NamedCheckboxAttribute, UserShortBadge, PagesSelection, ToolTip, Share, SelectTags, ListAndDictView, SchemaObjectCreator, schemaTypeObject,
    onServiceWorkerUpdateFound, CSS_COLOR, CategorySelectorStrip, CrammedUsersList, HorizontalUsersList, VerticalUsersList, TagsInput,
    TextAreaWithCheckBox, UserBadge, InfoText, EditableTextArea, RoundUserImage, EnabledWithCheckbox, addSchemaDefs, SCHEMA_DEFS, SimpleGrid, AutoInputOrTextArea, objToEl,
    CountryPhoneCodeSelect, SimpleMDE, MarkedView, JsonErrorTextView, SimpleTableEditor, ListOfItemsView, CodeViewer, CodeEditor, LocalStorageBackedInput
};
